import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Eye, Filter, X, Search, ArrowLeft, Calendar, User, AlertTriangle } from "lucide-react";
import { formatCurrency } from "@/components/inventory/InventoryUtils";
import { supabase } from "@/lib/supabase";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

export function CancelledOrdersList({ onBack }) {
  const [cancelledOrders, setCancelledOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    cancellationReason: "all",
    dateRange: {
      from: null,
      to: null,
    },
    representative: "all"
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [userData, setUserData] = useState(null);
  const [representatives, setRepresentatives] = useState([]);
  const [userNames, setUserNames] = useState({});
  const { toast } = useToast();
  const ITEMS_PER_PAGE = 40;

  const fetchUserData = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      if (!user) throw new Error('No hay usuario autenticado');

      const { data, error: roleError } = await supabase
        .from('users')
        .select('role, cod_rep, ver_comisiones_globales')
        .eq('id', user.id)
        .single();

      if (roleError) throw roleError;
      
      setUserData(data);
      return data;
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos del usuario",
        variant: "destructive",
      });
      return null;
    }
  };

  const fetchRepresentatives = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('name, cod_rep')
        .eq('role', 'vendedor')
        .order('name');

      if (error) throw error;
      setRepresentatives(data || []);
    } catch (error) {
      console.error('Error fetching representatives:', error);
    }
  };

  const fetchUserNames = async (userIds) => {
    try {
      if (!userIds || userIds.length === 0) return;

      // Obtener los nombres desde la tabla users del esquema public
      const { data, error } = await supabase
        .from('users')
        .select('id, name')
        .in('id', userIds);

      if (error) {
        console.error('Error fetching user names:', error);
        // Fallback en caso de error
        const namesMap = {};
        userIds.forEach(id => {
          namesMap[id] = `Usuario ${id.slice(0, 8)}...`;
        });
        setUserNames(prev => ({ ...prev, ...namesMap }));
        return;
      }

      const namesMap = {};
      data.forEach(user => {
        namesMap[user.id] = user.name;
      });

      setUserNames(prev => ({ ...prev, ...namesMap }));
    } catch (error) {
      console.error('Error fetching user names:', error);
      // Fallback en caso de error
      const namesMap = {};
      userIds.forEach(id => {
        namesMap[id] = `Usuario ${id.slice(0, 8)}...`;
      });
      setUserNames(prev => ({ ...prev, ...namesMap }));
    }
  };

  const fetchCancelledOrders = async () => {
    if (!userData) return;

    try {
      setLoading(true);
      
      let query = supabase
        .from('cancelled_orders')
        .select(`
          id,
          original_order_id,
          number,
          date,
          total,
          status,
          notes,
          fecha_entrega,
          forma_pago,
          cod_rep,
          signature,
          confirmacion_90dias,
          cancelled_at,
          cancelled_by,
          cancellation_reason,
          cancellation_notes,
          customer:customers(
            name,
            email,
            phone
          )
        `, { count: 'exact' })
        .order('cancelled_at', { ascending: false })
        .range((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE - 1);

      // Aplicar filtros
      if (userData?.role !== 'admin' && !userData?.ver_comisiones_globales) {
        query = query.eq('cod_rep', userData.cod_rep);
      } else if (filters.representative !== 'all') {
        query = query.eq('cod_rep', filters.representative);
      }

      if (filters.cancellationReason !== 'all') {
        query = query.eq('cancellation_reason', filters.cancellationReason);
      }

      if (filters.dateRange.from) {
        query = query.gte('cancelled_at', filters.dateRange.from.toISOString());
      }

      if (filters.dateRange.to) {
        query = query.lte('cancelled_at', filters.dateRange.to.toISOString());
      }

      // Aplicar búsqueda
      if (appliedSearchTerm.trim()) {
        const searchValue = appliedSearchTerm.trim();
        const isNumeric = !isNaN(searchValue) && searchValue !== '';
        
        if (isNumeric) {
          query = query.eq('number', parseInt(searchValue));
        } else {
          const { data: customerIds, error: customerError } = await supabase
            .from('customers')
            .select('id')
            .ilike('name', `%${searchValue}%`);

          if (customerError) throw customerError;

          if (customerIds && customerIds.length > 0) {
            const ids = customerIds.map(c => c.id);
            query = query.in('customer_id', ids);
          } else {
            setCancelledOrders([]);
            setTotalOrders(0);
            setTotalPages(1);
            setLoading(false);
            return;
          }
        }
      }

      const { data, error, count } = await query;

      if (error) throw error;

      setCancelledOrders(data || []);
      setTotalOrders(count || 0);
      setTotalPages(Math.ceil((count || 0) / ITEMS_PER_PAGE));

      // Obtener nombres de usuario para los pedidos cargados
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map(order => order.cancelled_by).filter(Boolean))];
        if (userIds.length > 0) {
          await fetchUserNames(userIds);
        }
      }
    } catch (error) {
      console.error('Error fetching cancelled orders:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los pedidos cancelados",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const user = await fetchUserData();
      if (user) {
        await Promise.all([
          fetchCancelledOrders(),
          fetchRepresentatives()
        ]);
      }
    };
    initialize();
  }, []);

  useEffect(() => {
    if (userData) {
      fetchCancelledOrders();
    }
  }, [filters, userData, currentPage, appliedSearchTerm]);

  useEffect(() => {
    if (userData) {
      setCurrentPage(1);
    }
  }, [filters, userData]);

  const handleSearch = () => {
    setAppliedSearchTerm(searchTerm);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setAppliedSearchTerm("");
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      cancellationReason: "all",
      dateRange: {
        from: null,
        to: null,
      },
      representative: "all"
    });
    setSearchTerm("");
    setAppliedSearchTerm("");
    setCurrentPage(1);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const getCancellationReasonBadge = (reason) => {
    const reasonMap = {
      'error': { 
        label: 'Error al introducir', 
        className: 'bg-red-100 text-red-800'
      },
      'client_cancellation': { 
        label: 'Anulación del cliente', 
        className: 'bg-orange-100 text-orange-800'
      },
      'partial_served_not_manage': {
        label: 'Parcialmente servido (no gestionar)',
        className: 'bg-amber-100 text-amber-800'
      }
    };

    const { label, className } = reasonMap[reason] || { 
      label: reason, 
      className: 'bg-gray-100 text-gray-800' 
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${className}`}>
        {label}
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return '';
    try {
      const parsedDate = typeof date === 'string' ? parseISO(date) : date;
      return format(parsedDate, "PPP", { locale: es });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const handleViewOrderDetails = async (order) => {
    try {
      // Obtener los items del pedido cancelado
      const { data: cancelledItems, error } = await supabase
        .from('cancelled_order_items')
        .select(`
          *,
          product:products(
            id,
            code,
            description,
            images,
            main_image_index
          )
        `)
        .eq('cancelled_order_id', order.id);

      if (error) throw error;

      setSelectedOrder({
        ...order,
        items: cancelledItems || []
      });
      setShowOrderDetails(true);
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los detalles del pedido",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Historial de Pedidos Cancelados</h2>
            <p className="text-gray-600">Pedidos anulados y guardados para auditoría</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <div className="flex gap-2 flex-1">
            <div className="flex gap-2 max-w-sm">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="search"
                  placeholder="Buscar por número de pedido o cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
              <Button
                variant="outline"
                onClick={handleSearch}
                className="flex items-center gap-2 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              >
                <Search className="h-4 w-4" />
                Buscar
              </Button>
              {appliedSearchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSearch}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            >
              <Filter className="h-4 w-4" />
              Filtros
              {(filters.cancellationReason !== "all" || filters.dateRange.from || filters.dateRange.to || filters.representative !== "all") && (
                <Badge variant="secondary" className="ml-2 bg-green-600 text-white">
                  {[
                    filters.cancellationReason !== "all",
                    filters.dateRange.from || filters.dateRange.to,
                    filters.representative !== "all"
                  ].filter(Boolean).length}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <span className="text-green-600">🔍</span>
                <h3 className="font-medium text-gray-800">Filtros</h3>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearFilters}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <X className="h-4 w-4 mr-2" />
                Limpiar filtros
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Motivo de cancelación</label>
                <Select
                  value={filters.cancellationReason}
                  onValueChange={(value) => handleFilterChange({ ...filters, cancellationReason: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="error">Error al introducir</SelectItem>
                    <SelectItem value="client_cancellation">Anulación del cliente</SelectItem>
                    <SelectItem value="partial_served_not_manage">Parcialmente servido (no gestionar)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Rango de fechas de cancelación</label>
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        {filters.dateRange.from ? (
                          formatDate(filters.dateRange.from)
                        ) : (
                          "Desde"
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={filters.dateRange.from}
                        onSelect={(date) =>
                          handleFilterChange({
                            ...filters,
                            dateRange: { ...filters.dateRange, from: date }
                          })
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        {filters.dateRange.to ? (
                          formatDate(filters.dateRange.to)
                        ) : (
                          "Hasta"
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={filters.dateRange.to}
                        onSelect={(date) =>
                          handleFilterChange({
                            ...filters,
                            dateRange: { ...filters.dateRange, to: date }
                          })
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {(userData?.role === 'admin' || userData?.ver_comisiones_globales) && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Representante</label>
                  <Select
                    value={filters.representative}
                    onValueChange={(value) => handleFilterChange({ ...filters, representative: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar representante" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {representatives.map((rep) => (
                        <SelectItem key={rep.cod_rep} value={rep.cod_rep}>
                          {rep.name} ({rep.cod_rep})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Vista de tabla */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="bg-red-50">
              <TableHead className="font-semibold text-red-800">Nº Pedido</TableHead>
              <TableHead className="font-semibold text-red-800">Cliente</TableHead>
              <TableHead className="font-semibold text-red-800">Fecha Cancelación</TableHead>
              <TableHead className="font-semibold text-red-800">Cancelado por</TableHead>
              <TableHead className="font-semibold text-red-800">Motivo</TableHead>
              <TableHead className="font-semibold text-red-800">Total</TableHead>
              <TableHead className="font-semibold text-red-800">Notas</TableHead>
              <TableHead className="text-right font-semibold text-red-800">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cancelledOrders.map((order) => (
              <TableRow key={order.id}>
                <TableCell className="font-medium">#{order.number}</TableCell>
                <TableCell>{order.customer?.name}</TableCell>
                <TableCell>{formatDate(order.cancelled_at)}</TableCell>
                                 <TableCell>{userNames[order.cancelled_by] || 'Usuario desconocido'}</TableCell>
                <TableCell>{getCancellationReasonBadge(order.cancellation_reason)}</TableCell>
                <TableCell className="font-bold text-red-600">{formatCurrency(order.total)}</TableCell>
                <TableCell className="max-w-xs truncate">
                  <div className="space-y-1">
                    {order.cancellation_notes && (
                      <p className="text-sm text-gray-600">{order.cancellation_notes}</p>
                    )}
                    {order.notes && (
                      <p className="text-xs text-gray-500">Original: {order.notes}</p>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleViewOrderDetails(order)}
                    title="Ver detalles"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginación */}
      <div className="flex items-center justify-between px-2">
        <div className="flex-1 text-sm text-muted-foreground">
          Mostrando {cancelledOrders.length} de {totalOrders} pedidos cancelados
          {appliedSearchTerm && (
            <span className="ml-2 text-blue-600">
              (buscando: "{appliedSearchTerm}")
            </span>
          )}
        </div>
        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Página {currentPage} de {totalPages}</p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              Primera
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </Button>
            
            {/* Números de página */}
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className="w-8 h-8 p-0"
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Siguiente
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Última
            </Button>
          </div>
        </div>
      </div>

      {/* Diálogo de detalles del pedido */}
      <Dialog open={showOrderDetails} onOpenChange={setShowOrderDetails}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Detalles del Pedido Cancelado #{selectedOrder?.number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Información general */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-900">Información del Pedido</h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium">Número:</span> #{selectedOrder.number}</p>
                    <p><span className="font-medium">Fecha original:</span> {formatDate(selectedOrder.date)}</p>
                    <p><span className="font-medium">Fecha cancelación:</span> {formatDate(selectedOrder.cancelled_at)}</p>
                    <p><span className="font-medium">Total:</span> {formatCurrency(selectedOrder.total)}</p>
                                         <p><span className="font-medium">Cancelado por:</span> {userNames[selectedOrder.cancelled_by] || 'Usuario desconocido'}</p>
                    <p><span className="font-medium">Motivo:</span> {getCancellationReasonBadge(selectedOrder.cancellation_reason)}</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-900">Información del Cliente</h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium">Nombre:</span> {selectedOrder.customer?.name}</p>
                    <p><span className="font-medium">Email:</span> {selectedOrder.customer?.email}</p>
                    <p><span className="font-medium">Teléfono:</span> {selectedOrder.customer?.phone}</p>
                  </div>
                </div>
              </div>

              {/* Notas */}
              {(selectedOrder.cancellation_notes || selectedOrder.notes) && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-900">Notas</h3>
                  <div className="space-y-2 text-sm">
                    {selectedOrder.cancellation_notes && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="font-medium text-red-800">Notas de cancelación:</p>
                        <p className="text-red-700">{selectedOrder.cancellation_notes}</p>
                      </div>
                    )}
                    {selectedOrder.notes && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <p className="font-medium text-gray-800">Notas originales:</p>
                        <p className="text-gray-700">{selectedOrder.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Items del pedido */}
              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-900">Productos del Pedido</h3>
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Producto</TableHead>
                          <TableHead>Talla</TableHead>
                          <TableHead>Color</TableHead>
                          <TableHead>Cantidad cancelada</TableHead>
                          <TableHead>Precio</TableHead>
                          <TableHead>Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedOrder.items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{item.product?.code}</p>
                                <p className="text-sm text-gray-600">{item.product?.description}</p>
                              </div>
                            </TableCell>
                            <TableCell>{item.size}</TableCell>
                            <TableCell>{item.color}</TableCell>
                            <TableCell>{item.quantity}</TableCell>
                            <TableCell>{formatCurrency(item.price)}</TableCell>
                            <TableCell className="font-medium">
                              {formatCurrency(item.quantity * item.price)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
} 
